import { forwardRef, useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

type PasswordInputProps = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type"
> & {
    error?: boolean;
    iconLeft?: boolean;
    showToggleLabel?: string;
    hideToggleLabel?: string;
};

/**
 * PasswordInput
 *
 * A password field with a persistent lock icon on the left and a
 * show/hide toggle on the right. Password is hidden by default.
 * Fully controlled by parent form (works with react-hook-form's register).
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    function PasswordInput(
        {
            className,
            error,
            iconLeft = true,
            showToggleLabel = "Show password",
            hideToggleLabel = "Hide password",
            id,
            ...props
        },
        ref,
    ) {
        const [visible, setVisible] = useState(false);

        return (
            <div className="relative">
                {iconLeft ? (
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                ) : null}
                <input
                    id={id}
                    ref={ref}
                    type={visible ? "text" : "password"}
                    aria-invalid={error || undefined}
                    className={cn(
                        // Match shadcn Input styling exactly so both inputs align visually.
                        "flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background transition placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                        iconLeft && "pl-9",
                        "pr-11",
                        error &&
                            "border-error focus-visible:ring-error/60",
                        className,
                    )}
                    {...props}
                />
                <button
                    type="button"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? hideToggleLabel : showToggleLabel}
                    aria-pressed={visible}
                    data-testid="password-toggle"
                    tabIndex={-1}
                    className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                    {visible ? (
                        <EyeOff className="h-4 w-4" />
                    ) : (
                        <Eye className="h-4 w-4" />
                    )}
                </button>
            </div>
        );
    },
);

export default PasswordInput;
